import { pickUnderlyingVideoElement } from '@src/utility/stackedLayout';

import { TimelineMode } from '../commands/TimelineMode';
import { Element } from '../models/Element';
import { LogoElement } from '../models/elements/LogoElement';
import { VideoElement } from '../models/elements/VideoElement';
import { ExtraElementData } from '../models/ExtraElementData';
import { PunchListItem } from '../models/PunchListItem';
import { KARAOKE_TRACK_NUMBER } from '../models/Timeline';
import { EditorChangeType } from '../types';

import type { AudioElement } from '../models';
import type { Track } from '../models/Track';
import type { TranscriptToken } from '../types';
import type { TrackType } from '../types/TrackType';
import type { VideoEditor } from '../VideoEditor';
import type {
  PunchListItem as PunchListItemData,
  Video,
} from '@src/types.ts/story';

/**
 * Configuration for ripple behavior with transition overlap support.
 * When provided, adjusts the ripple amount to account for transitions.
 */
export interface RippleConfig {
  /** Overlap at the start (element overlaps previous content) in seconds */
  overlapBefore: number;
  /** Overlap at the end (next content overlaps element) in seconds */
  overlapAfter: number;
}

/**
 * TrackManager is a utility class responsible for managing tracks and elements
 * within a Timeline in the video editing context.
 *
 * It provides functionality to create and remove tracks, add or remove elements,
 * merge multiple elements into one, and split an element into multiple parts.
 * The class integrates with the VideoEditor's batching mechanism to optimize
 * performance and maintain state consistency.
 */
export class TrackManager {
  /**
   * Instance variables for Timeline and VideoEditor
   */
  protected editor: VideoEditor;

  protected _extraElementData: ExtraElementData | null = null;

  /**
   * Map of punch list items by element ID.
   * These are runtime domain models hydrated from extraElementData.
   */
  private _punchListItems: Map<string, PunchListItem> = new Map();

  /**
   * Creates a new TrackManager instance
   * @param editor Optional VideoEditor reference for dirty marking and notifications
   */
  constructor(editor: VideoEditor) {
    this.editor = editor;
  }

  /**
   * Small tolerance value for floating-point comparisons
   * @protected
   */
  protected epsilon: number = 0.001;

  /**
   * Marks the timeline as dirty if an editor is available
   * @param source Description of what caused the change
   */
  markDirty(source: string): void {
    if (this.editor) {
      this.editor.markDirty(`trackManager: ${source}`);
    }
  }

  /**
   * Notifies observers of a change if an editor is available
   * @param changeType Type of change that occurred
   * @param payload Additional data about the change
   */
  notifyObservers(changeType: EditorChangeType, payload?: any): void {
    if (this.editor) {
      this.editor.notifyObservers(changeType, payload);
    }
  }

  //
  // External interfaces - public methods for timeline operations
  //
  /**
   * Moves an element to a new position on the timeline
   * @param element Element to move
   * @param newStartTime New start time for the element
   * @param mode Optional TimelineMode to use for this operation
   * @param snapToFrame Whether to snap to frame boundaries
   * @param targetTrackId Optional track id if moving between tracks
   * @returns true if successful, false otherwise
   */
  public shiftElement(
    element: Element,
    newStartTime: number,
    mode: TimelineMode = TimelineMode.DEFAULT,
    targetTrackNumber?: number,
    snapToFrame: boolean = true,
  ): boolean {
    if (!element) return false;

    // Get source track
    const sourceTrack = this.editor.timeline.getTrackForElement(element.id);
    if (!sourceTrack) return false;

    const destinationTrack =
      targetTrackNumber !== undefined
        ? this.editor.timeline.getTrack(String(targetTrackNumber))
        : sourceTrack;

    if (!destinationTrack) return false;

    // Snap time to frame and ensure it's non-negative
    if (snapToFrame) {
      newStartTime = this.editor.timeline.snapTimeToFrame(newStartTime);
    }
    newStartTime = Math.max(0, newStartTime);

    // Store original values for change notification
    const originalTime = element.time;
    const originalTrackId = sourceTrack.id;

    // Handle track change if needed
    if (destinationTrack.id !== sourceTrack.id) {
      if (!this.moveElementToTrack(element, destinationTrack)) {
        return false;
      }
    }

    // Determine final position based on timeline mode
    let finalStartTime =
      mode === TimelineMode.OVERLAP || mode === TimelineMode.RIPPLE
        ? newStartTime
        : this.calculateValidStartTime(
            destinationTrack,
            element,
            newStartTime,
            element.time,
            element.durationSeconds,
          );

    // Check if movement is necessary
    const shiftDistance = finalStartTime - element.time;
    if (shiftDistance === 0 && destinationTrack.id === sourceTrack.id) {
      return true;
    }

    // Apply the shift based on mode
    switch (mode) {
      case TimelineMode.DEFAULT:
        // In DEFAULT mode, we've already calculated a valid finalStartTime that avoids collisions
        element.update({ time: finalStartTime });
        console.log(`moving element ${element.id} to ${finalStartTime}`);
        break;

      case TimelineMode.RIPPLE: {
        // Shift the element to the snapped position
        element.update({ time: finalStartTime });
        // Shift elements after the new position
        const affectedElements = this.getElementsAfterTime(
          destinationTrack,
          finalStartTime,
          element,
        );
        // Only shift if there are elements to move
        if (affectedElements.length > 0) {
          this.applyTimeDeltaToElements(affectedElements, shiftDistance);
        }
        break;
      }

      case TimelineMode.OVERLAP:
        element.update({ time: finalStartTime });
        break;
    }

    // Update timeline duration
    this.editor.timeline.recalculateDuration();

    // Mark as dirty
    this.markDirty(`moveElement (${element.id})`);

    // Notify observers about the element move
    this.notifyObservers(EditorChangeType.ELEMENT_SHIFTED, {
      element: element,
      previousTime: originalTime,
      newTime: finalStartTime,
      previousTrackId: originalTrackId,
      newTrackId: destinationTrack.id,
      moveDistance: shiftDistance,
      mode: mode,
    });

    return true;
  }

  public changeElementPlacements(
    placements: {
      element: Element;
      time: number;
    }[],
  ): boolean {
    if (placements.length === 0) return true;

    // Apply placements - validation happens in ChangeElementPlacementsCommand
    for (const { element, time } of placements) {
      element.update({ time });
    }

    return true;
  }

  /**
   * Trims the start of an element, adjusting start time and duration
   * Handles both general and video-specific trimming logic
   * @param element The element to trim
   * @param newStartTime New start time for the element
   * @param mode Optional TimelineMode to use for this operation
   * @returns true if successful, false otherwise
   */
  public trimElementStart(
    element: Element,
    newStartTime: number,
    mode: TimelineMode = TimelineMode.DEFAULT,
    snapToFrame: boolean = true,
  ): boolean {
    if (!element) return false;

    // Ensure time is non-negative and frame-snapped
    if (snapToFrame) {
      newStartTime = this.editor.timeline.snapTimeToFrame(newStartTime);
    }
    newStartTime = Math.max(0, newStartTime);

    // Get track
    const track = this.getTrackById(
      this.editor.timeline.getTrackIdForElement(element.id) || '',
    );
    if (!track) return false;

    // Handle elements with no duration (e.g., markers)
    if (element.duration === null) {
      // Store original state for notifications
      const originalTime = element.time;

      element.update({ time: newStartTime });

      // Mark as dirty and notify
      this.markDirty(`trimElementStart (${element.id}) - marker element`);
      this.notifyObservers(EditorChangeType.ELEMENT_TRIMMED_START, {
        element: element,
        previousTime: originalTime,
        newTime: newStartTime,
        mode: mode,
      });

      return true;
    }

    // Extract original properties
    const originalTime = element.time;
    const originalDuration = element.durationSeconds;
    const isVideoOrAudio = element.type === 'video' || element.type === 'audio';
    const originalTrimStart = isVideoOrAudio ? (element.trim_start ?? 0) : 0;

    // Calculate potential trim_start based on desired new start time
    const timeDiff = newStartTime - originalTime;
    const potentialTrimStart = isVideoOrAudio
      ? originalTrimStart + timeDiff
      : 0;

    // Determine snapped start time based on mode (initially use newStartTime)
    let snappedStartTime = newStartTime;
    let newDuration = originalDuration - timeDiff; // Default duration adjustment

    // Adjust properties for video/audio elements if trim_start would be negative
    let newTrimStart: number | undefined;
    if (isVideoOrAudio) {
      if (potentialTrimStart >= 0) {
        // No adjustment needed; use the potential trim_start
        newTrimStart = potentialTrimStart;
        newDuration = originalDuration - timeDiff;
        snappedStartTime = newStartTime;
      } else {
        // Clamp trim_start to 0 and adjust time and duration
        newTrimStart = 0;
        const trimAdjustment = Math.abs(potentialTrimStart);
        snappedStartTime = newStartTime + trimAdjustment; // e.g., 0 + abs(-2) = 2
        newDuration = originalDuration + originalTrimStart;
      }
    } else {
      // Non-video/audio elements just adjust duration
      newDuration = originalDuration - timeDiff;
    }

    // In DEFAULT mode, snap the start time to avoid collisions
    if (mode === TimelineMode.DEFAULT) {
      snappedStartTime = this.calculateValidStartTime(
        track,
        element,
        snappedStartTime,
        originalTime,
        newDuration,
      );
      // Recalculate timeDiff after snapping
      const adjustedTimeDiff = snappedStartTime - originalTime;
      if (isVideoOrAudio) {
        const adjustedPotentialTrimStart = originalTrimStart + adjustedTimeDiff;
        if (adjustedPotentialTrimStart >= 0) {
          newTrimStart = adjustedPotentialTrimStart;
          newDuration = originalDuration - adjustedTimeDiff;
        } else {
          newTrimStart = 0;
          const trimAdjustment = Math.abs(adjustedPotentialTrimStart);
          snappedStartTime = snappedStartTime + trimAdjustment;
          newDuration = newDuration + trimAdjustment;
        }
      } else {
        newDuration = originalDuration - adjustedTimeDiff;
      }
    }

    // Validate new duration
    if (newDuration <= 0) return false;

    // Prepare updates
    const updates: Record<string, any> = {
      time: snappedStartTime,
      duration: newDuration,
    };
    if (isVideoOrAudio) {
      updates.trim_start = newTrimStart;
    }

    element.update(updates);

    // TODO: remove after transcript is fully refactored
    // Refresh transcript segment for video elements
    if (element.type === 'video') {
      (element as VideoElement).invalidateTranscriptSegment();
    }

    // Apply ripple effect in RIPPLE mode
    if (mode === TimelineMode.RIPPLE) {
      const affectedElements = this.getElementsAfterTime(
        track,
        element.end_time,
        element,
      );
      this.applyTimeDeltaToElements(
        [element, ...affectedElements],
        -(newStartTime - originalTime),
      );
    }

    this.editor.timeline.recalculateDuration();

    // Mark as dirty
    this.markDirty(`trimElementStart (${element.id})`);

    // Notify observers
    this.notifyObservers(EditorChangeType.ELEMENT_TRIMMED_START, {
      element: element,
      previousTime: originalTime,
      previousDuration: originalDuration,
      newTime: snappedStartTime,
      newDuration: newDuration,
      previousTrimStart: originalTrimStart,
      newTrimStart: newTrimStart,
      mode: mode,
    });

    return true;
  }

  /**
   * Trims the media start of a video or audio element, keeping the timeline start time fixed,
   * adjusting duration, and optionally rippling subsequent elements.
   * @param element The video or audio element to trim
   * @param newTrimStart New trim_start value (seconds into the media)
   * @param mode Optional TimelineMode (RIPPLE shifts subsequent elements)
   * @returns true if successful, false otherwise
   */
  public trimMediaStart(
    element: Element,
    newTrimStart: number,
    mode: TimelineMode = TimelineMode.DEFAULT,
    snapToFrame: boolean = true,
  ): boolean {
    // Only video or audio elements support trim_start, compositions can have video children
    if (
      !element ||
      (element.type !== 'video' &&
        element.type !== 'audio' &&
        element.type !== 'composition')
    )
      return false;

    // Ensure newTrimStart is non-negative and frame-snapped
    if (snapToFrame) {
      newTrimStart = this.editor.timeline.snapTimeToFrame(newTrimStart);
    }
    newTrimStart = Math.max(0, newTrimStart);

    // Get track
    const track = this.getTrackById(
      this.editor.timeline.getTrackIdForElement(element.id) || '',
    );
    if (!track) return false;

    // Extract original properties
    const originalTrimStart =
      pickUnderlyingVideoElement(element)?.trim_start ?? 0;
    const originalDuration = element.durationSeconds;
    const originalEndTime = element.end_time;

    // Calculate trim difference and new duration
    const trimDiff = newTrimStart - originalTrimStart;
    const newDuration = originalDuration - trimDiff;

    // Validate new duration
    if (newDuration <= 0) return false;

    // Split updates, they might be destined for different objects
    element.update({
      duration: newDuration,
    });
    pickUnderlyingVideoElement(element)?.update({
      trim_start: newTrimStart,
    });

    // TODO: remove after transcript is fully refactored
    // Refresh transcript segment for video elements
    if (element.type === 'video') {
      (element as VideoElement).invalidateTranscriptSegment();
    }

    // In RIPPLE mode, shift subsequent elements
    if (mode === TimelineMode.RIPPLE) {
      const affectedElements = this.getElementsAfterTime(
        track,
        originalEndTime,
        element,
      );
      this.applyTimeDeltaToElements(affectedElements, -trimDiff);
    }

    this.editor.timeline.recalculateDuration();
    this.markDirty(`trimMediaStart (${element.id})`);

    // Notify observers
    this.notifyObservers(EditorChangeType.ELEMENT_TRIMMED_MEDIA_START, {
      element: element,
      previousTrimStart: originalTrimStart,
      newTrimStart: newTrimStart,
      previousDuration: originalDuration,
      newDuration: newDuration,
      mode: mode,
    });

    return true;
  }

  /**
   * Adjusts the end of an element (both extending and trimming)
   * Handles both general and video-specific duration adjustment logic
   * @param element The element to adjust
   * @param newEndTime New end time for the element
   * @param mode Optional TimelineMode to use for this operation
   * @returns true if successful, false otherwise
   */
  public trimElementEnd(
    element: Element,
    newEndTime: number,
    mode: TimelineMode = TimelineMode.DEFAULT,
    snapToFrame: boolean = true,
  ): boolean {
    if (!element) return false;

    // Apply frame snapping
    if (snapToFrame) {
      newEndTime = this.editor.timeline.snapTimeToFrame(newEndTime);
    }
    newEndTime = Math.max(0, newEndTime);

    const track = this.getTrackById(
      this.editor.timeline.getTrackIdForElement(element.id) || '',
    );
    if (!track) return false;

    if (newEndTime < element.time) return false; // Prevent invalid end time

    // Capture original state for video-specific calculations
    const currentEndTime = element.end_time;

    // Calculate the new duration
    let newDuration = newEndTime - element.time;

    // For DEFAULT mode with extension, handle snapping
    if (
      mode == TimelineMode.DEFAULT &&
      element.duration !== null &&
      newDuration > element.durationSeconds
    ) {
      // Treat as extend
      newDuration = this.calculateValidDuration(
        track,
        element,
        newEndTime,
        element.time,
        element.durationSeconds,
      );
    }

    if (newDuration <= 0) return false; // Prevent invalid duration

    // Final end time after any adjustments
    const finalEndTime = element.time + newDuration;

    // Apply all updates at once
    element.update({ duration: newDuration });

    // Ensure transcript segment is refreshed if it's a video element
    // TODO: remove after transcript is fully refactored
    if (element.type === 'video') {
      const videoElement = element as VideoElement;
      videoElement.invalidateTranscriptSegment();
    }

    // Apply ripple effect if in ripple mode
    if (mode === TimelineMode.RIPPLE) {
      const timeDelta = finalEndTime - currentEndTime; // Positive if extending, negative if trimming

      if (timeDelta != 0) {
        const affectedElements = this.getElementsAfterTime(
          track,
          currentEndTime,
          element,
        );
        this.applyTimeDeltaToElements(affectedElements, timeDelta);
      }
    }

    // Recalculate timeline duration
    this.editor.timeline.recalculateDuration();

    return true;
  }

  /**
   * Calculates a start time that avoids collisions with other elements on the track
   * @param track The track to check for collisions
   * @param element The element being adjusted (to exclude from collision check)
   * @param requestedStartTime Requested start time
   * @param originalStartTime Original start time of the element
   * @param duration Duration of the element
   * @returns A valid start time that avoids collisions
   */
  calculateValidStartTime(
    track: Track,
    element: Element,
    requestedStartTime: number,
    originalStartTime: number,
    duration: number,
  ): number {
    // Check moving forward...
    if (requestedStartTime > originalStartTime) {
      // Check following collisons
      const followingElements = this.getFollowingElements(element);
      const closestFollowingElement = followingElements[0];
      for (const nextElement of followingElements) {
        const nextElementStart = nextElement.time;
        // Check for collision between [requestedStartTime, requestedStartTime + duration] and [nextElement.time. nextElement.end_time]
        if (
          requestedStartTime < nextElement.end_time &&
          requestedStartTime + duration > nextElementStart
        ) {
          // Collision detected, move as close to requested time as possible
          // Example: originalTime 6, duration 3, requestedTime = 10, next element starts at 12, duration 3
          //          10 + 3 = 13, 13 > 12, so move to 12 - 3 = 9
          return closestFollowingElement.time - duration;
        }
      }
      // And backward
    } else {
      // Check previous collisions
      const previousElements = this.getPrecedingElements(element);
      const closestPrevElement = previousElements[previousElements.length - 1];

      for (const prevElement of previousElements) {
        // Check for collision between [requestedStartTime, requestedStartTime + duration] and [prevElement.time, prevElement.end_time]
        if (
          requestedStartTime < prevElement.end_time &&
          requestedStartTime + duration > prevElement.time
        ) {
          // Collision detected, move as close to requested time as possible
          // Example: originalTime 6, duration 3, requestedTime = 2, previous element starts at 0, duration 3
          //          2 + 3 = 5, 5 > 0, so move to 0 + 3 = 3
          return closestPrevElement.end_time;
        }
      }
    }

    return requestedStartTime;
  }

  /**
   * Calculates a duration that avoids collisions with other elements on the track
   * @param track The track to check for collisions
   * @param element The element being adjusted (to exclude from collision check)
   * @param requestedEndTime Requested end time
   * @param originalStartTime Original start time of the element
   * @param duration Duration of the element
   * @returns A valid duration that avoids collisions
   */
  protected calculateValidDuration(
    track: Track,
    element: Element,
    requestedEndTime: number,
    originalStartTime: number,
    duration: number,
  ): number {
    // Calculate the requested duration using the element's start time
    const requestedDuration = requestedEndTime - originalStartTime;

    // Prevent negative duration
    if (requestedDuration <= 0) {
      return 0;
    }

    const originalEndTime = originalStartTime + duration;

    //  Handle extension beyond the original end time
    if (requestedEndTime > originalEndTime) {
      // Assume track has a method to get elements following this one
      const followingElements = this.getFollowingElements(element);
      const nextElement = followingElements[0];

      if (nextElement) {
        const nextElementStart = nextElement.time;
        if (requestedEndTime > nextElementStart) {
          // Collision detected: limit duration to the start of the next element
          return nextElementStart - originalStartTime;
        }
      }
    }

    // Return the requested duration if no collisions
    return requestedDuration;
  }
  /**
   * Creates a new track of the specified type and adds it to the timeline
   * @param type The type of track to create
   * @returns The newly created Track object
   */
  public createTrack(
    type: 'video' | 'audio' | 'text' | 'image' | 'shape' = 'video',
  ): Track {
    const track = this.editor.timeline.createTrack(type);

    // Mark as dirty
    this.markDirty(`createTrack (${track.id})`);

    // Notify observers
    this.notifyObservers(EditorChangeType.TRACK_ADDED, {
      track: track,
      trackId: track.id,
      type: type,
    });

    return track;
  }

  /**
   * Creates a new track above all existing tracks (highest track number + 1)
   * This ensures new tracks are always placed at the top of the timeline
   * @param type The type of track to create
   * @returns The newly created Track object
   */
  public createTrackAboveAll(type: TrackType = 'video'): Track {
    // Get the highest existing track number and add 1
    const highestTrackNumber = this.getHighestTrackNumber();
    const newTrackId = String(highestTrackNumber + 1);

    // Validate that the new track ID is within allowed range
    if (highestTrackNumber + 1 >= 32) {
      // KARAOKE_TRACK_NUMBER = 32
      throw new Error(
        `Cannot create track above all existing tracks. Track ID ${newTrackId} would exceed maximum allowed (31).`,
      );
    }

    // Use the existing createTrack method with trackIdOverride
    const track = this.editor.timeline.createTrack(type, newTrackId);

    // Mark as dirty
    this.markDirty(`createTrackAboveAll (${track.id})`);

    // Notify observers
    this.notifyObservers(EditorChangeType.TRACK_ADDED, {
      track: track,
      trackId: track.id,
      type: type,
    });

    return track;
  }

  /**
   * Gets the highest existing track number
   * @param ceil Optional ceiling value to exclude tracks above or equal to it
   * (defaults to karaoke track number)
   * @returns 0 if no tracks exist
   */
  protected getHighestTrackNumber(ceil: number = KARAOKE_TRACK_NUMBER): number {
    const timeline = this.editor.timeline;
    if (timeline.tracks.length === 0) {
      return 0;
    }

    // Convert string IDs to numbers, filter out excluded, and find the highest
    const trackIds = timeline.tracks
      .map(track => Number(track.id))
      .filter(id => !Number.isNaN(id) && id < ceil); // Only consider valid IDs below ceiling value

    return trackIds.length > 0 ? Math.max(...trackIds) : 0;
  }

  public isTopmostTrack(track: Track): boolean {
    // Get the highest track number
    const highestTrackNumber = this.getHighestTrackNumber();
    // Check if the track's ID matches the highest track number
    return track.id === String(highestTrackNumber);
  }

  public getTopmostTrack(): Track | undefined {
    // Get the highest track number
    const highestTrackNumber = this.getHighestTrackNumber();
    // Find the track with that ID
    return this.editor.timeline.getTrack(String(highestTrackNumber));
  }

  public isLogoTrack(track: Track): boolean {
    return (
      track.elements.length > 0 &&
      track.elements.every(el => Element.isLogoElement(el))
    );
  }

  public getPrevTrack(currentTrack: Track): Track | undefined {
    // Get the highest track number relative to currentTrack
    const highestTrackNumber = this.getHighestTrackNumber(
      Number(currentTrack.id),
    );
    // Find the track with that ID
    return this.editor.timeline.getTrack(String(highestTrackNumber));
  }

  /**
   * Removes a track from the timeline
   * @param track The track to remove
   * @returns true if removed, false if the track doesn't exist
   */
  public removeTrack(track: Track): boolean {
    // Store track data for notification if needed.
    // Send the Track itself, never a spread copy: `id`, `type`, `name` and
    // `elements` are getters over private fields, so `{ ...track }` drops all of
    // them and hands observers an object whose `id` is undefined. Removing a
    // track does not mutate it, so the instance still describes what was removed.
    const trackElements = [...track.elements];

    // Remove the track from the timeline
    const success = this.editor.timeline.removeTrack(track.id);

    if (success) {
      // Mark as dirty
      this.markDirty(`removeTrack (${track.id})`);

      // Notify observers
      this.notifyObservers(EditorChangeType.TRACK_REMOVED, {
        trackId: track.id,
        track,
        elements: trackElements,
      });
    }

    return success;
  }

  /**
   * Moves an element to a different track
   * @param element The element to move
   * @param targetTrack The target track
   * @returns true if successful, false otherwise
   */
  public moveElementToTrack(element: Element, targetTrack: Track): boolean {
    // Get source track ID
    const sourceTrackId = this.editor.timeline.getTrackIdForElement(element.id);
    if (!sourceTrackId) return false;

    // If source and target are the same, nothing to do
    if (sourceTrackId === targetTrack.id) return true;

    // Store original track for notifications
    const sourceTrack = this.editor.timeline.getTrack(sourceTrackId);
    if (!sourceTrack) return false;

    // Execute the move
    const success = this.editor.timeline.moveElementToTrack(
      element.id,
      targetTrack.id,
    );

    if (success) {
      // Mark as dirty
      this.markDirty(`moveElementToTrack (${element.id})`);

      // Notify observers
      this.notifyObservers(EditorChangeType.ELEMENT_TRACK_CHANGED, {
        element: element,
        previousTrack: sourceTrack,
        newTrack: targetTrack,
      });
    }

    return success;
  }

  /**
   * Removes an element from the timeline and optionally ripples subsequent elements.
   * @param element The element to remove.
   * @param mode The timeline mode:
   *   - DEFAULT: Just removes the element, no shifting
   *   - RIPPLE: Shifts subsequent elements on the same track to cover the gap
   *   - RIPPLE_ALL_TRACKS: Shifts subsequent elements on ALL tracks to cover the gap
   * @returns true if the element was successfully removed, false otherwise.
   */
  public removeElement(
    element: Element,
    mode: TimelineMode = TimelineMode.DEFAULT,
    /**
     * Overlaps to reverse, for a caller that knows them better than the
     * element's meta does — `AddMediaElementCommand` rolling back its own add
     * holds the exact config it applied, and the meta may not be written yet.
     * Everyone else omits it and gets the meta (see below).
     */
    rippleConfig?: RippleConfig,
  ): boolean {
    // Capture the element's start time and duration before removal
    const startTime = element.time;
    const duration = element.durationSeconds;

    // Get the track containing the element
    const track = this.editor.timeline.getTrackForElement(element.id);
    if (!track) return false;

    // Remove the element from the track
    track.removeElement(element.id);

    // Calculate ripple amount, accounting for transition overlaps, mirroring the
    // forward operation in addElement.
    //
    // Absent an explicit config, the overlaps come off the element's own meta.
    // `addElement` takes them from the template because the element does not
    // exist yet; by the time a user removes it they are persisted, so reading
    // them here means no caller has to remember to supply them. Reversing the
    // full duration instead left the timeline short by the overlap — enough to
    // close the gap at a transcript cut and duplicate the words either side of
    // it (PLAT-11535).
    const { overlapBefore, overlapAfter } = rippleConfig ??
      this._extraElementData?.getRippleOverlaps(element) ?? {
        overlapBefore: 0,
        overlapAfter: 0,
      };
    const rawRippleAmount = duration - overlapBefore - overlapAfter;
    const rippleAmount = Math.max(0, rawRippleAmount);

    // Handle ripple mode if duration is positive
    if (mode === TimelineMode.RIPPLE && rippleAmount > 0) {
      // Get all elements that start after the removed element's start time
      const elementsAfter = this.getElementsAfterTime(
        track,
        startTime,
        element,
      );
      // Shift them earlier by the ripple amount (accounting for overlaps)
      this.applyTimeDeltaToElements(elementsAfter, -rippleAmount);
    }
    // Handle ripple all tracks mode
    if (mode === TimelineMode.RIPPLE_ALL_TRACKS && rippleAmount > 0) {
      // Collect every track's elements first, then shift once. The zero floor
      // in `applyTimeDeltaToElements` is computed over the elements it is
      // handed, so calling it per track gave each track its own floor: one
      // whose earliest element sat at 0 absorbed the shift while the rest took
      // it in full, and the tracks drifted apart relative to each other. One
      // call means one floor and one delta for the whole timeline.
      const elementsToShift = this.editor.timeline.tracks.flatMap(t =>
        this.getElementsAfterTime(t, startTime, element),
      );

      this.applyTimeDeltaToElements(elementsToShift, -rippleAmount);
    }

    // Update the timeline duration
    this.editor.timeline.recalculateDuration();

    // Mark the timeline as dirty
    this.markDirty(`removeElement (${element.id})`);

    // Notify any observers of the change
    this.notifyObservers(EditorChangeType.ELEMENT_REMOVED, {
      elementId: element.id,
      mode: mode,
    });

    return true;
  }

  /**
   * Changes an element's duration and shifts everything after it, on ALL
   * tracks, by the difference — the resize counterpart of a
   * `RIPPLE_ALL_TRACKS` add/remove. A blank media card (PLAT-11732) uses it to
   * take its uploaded video's length.
   *
   * "After it" is anything starting at or past where the element's end lets the
   * next content in: `end_time - overlapAfter`, with the overlap read off the
   * element's meta the same way `removeElement` reads it. Content the element
   * merely overlaps (music under an intro, a coupled logo) stays put.
   *
   * @returns false when the duration is not a positive number
   */
  public resizeElementRippleAllTracks(
    element: Element,
    newDuration: number,
  ): boolean {
    if (!Number.isFinite(newDuration) || newDuration <= 0) return false;

    const delta = newDuration - element.durationSeconds;
    const { overlapAfter } = this._extraElementData?.getRippleOverlaps(
      element,
    ) ?? { overlapAfter: 0 };
    const threshold = element.end_time - overlapAfter;
    // Collected before the resize moves `end_time`, and shifted as one group so
    // the tracks keep their spacing (see `applyTimeDeltaToElements`).
    const elementsToShift = this.editor.timeline.tracks.flatMap(t =>
      this.getElementsAfterTime(t, threshold, element),
    );

    element.update({ duration: newDuration });
    if (delta !== 0) this.applyTimeDeltaToElements(elementsToShift, delta);

    this.editor.timeline.recalculateDuration();
    this.markDirty(`resizeElementRippleAllTracks (${element.id})`);
    this.notifyObservers(EditorChangeType.ELEMENT_UPDATED, {
      elementId: element.id,
      element,
      properties: { duration: newDuration },
    });
    return true;
  }

  /**
   * Adds an element to a track at its specified time, optionally rippling subsequent elements.
   * @param element The element to add
   * @param track The target track
   * @param mode TimelineMode to determine rippling behavior
   * @param rippleConfig Config for overlap adjustments and selective audio handling
   * @returns true if successful, false otherwise
   */
  public addElement(
    element: Element,
    track: Track,
    mode: TimelineMode = TimelineMode.DEFAULT,
    rippleConfig?: RippleConfig,
  ): boolean {
    // Calculate ripple amount, accounting for transition overlaps if provided
    // rippleAmount = duration - startOverlap - endOverlap
    const startOverlap = rippleConfig?.overlapBefore ?? 0;
    const endOverlap = rippleConfig?.overlapAfter ?? 0;
    const rippleAmount = element.durationSeconds - startOverlap - endOverlap;

    if (mode === TimelineMode.RIPPLE) {
      const elementsAfter = this.getElementsAfterTime(
        track,
        element.time,
        element,
      );
      this.applyTimeDeltaToElements(elementsAfter, rippleAmount);
    }
    if (mode === TimelineMode.RIPPLE_ALL_TRACKS) {
      this.editor.timeline.tracks.forEach(t => {
        const elementsToShift = this.getElementsAfterTime(
          t,
          element.time,
          element,
        );

        this.applyTimeDeltaToElements(elementsToShift, rippleAmount);
      });
    }

    this.editor.timeline.addElementToTrack(track.id, element);
    this.editor.timeline.recalculateDuration();
    this.markDirty(`addElementWithRipple (${element.id})`);
    this.notifyObservers(EditorChangeType.ELEMENT_ADDED, {
      element,
      trackId: track.id,
      mode,
    });
    return true;
  }

  /**
   * Merges multiple elements into a single element on the specified track.
   * Elements must be contiguous (no gaps between them).
   *
   * @param elements Array of elements to merge - only audio or video elements with same source can be merged
   * @param targetTrack The track where the merged element will reside
   * @returns The merged Element or null if the operation fails
   */
  public mergeElements<
    ElementType extends
      | InstanceType<typeof VideoElement>
      | InstanceType<typeof AudioElement>,
  >(elements: ElementType[], targetTrack: Track): ElementType | null {
    if (!this.validateMerge(elements)) {
      console.warn('Cannot merge elements: validation failed');
      return null;
    }

    const mergedProps = this.calculateMergedProperties(elements);

    // Create a new element based on the first element but with merged properties
    const mergedElement = elements[0].clone();
    mergedElement.update(mergedProps);

    // Ensure the new element has a unique ID
    mergedElement.id = `merged-${Date.now()}-${Math.random()
      .toString(36)
      .substring(2, 7)}`;

    // Remove all original elements
    elements.forEach(element => this.editor.timeline.removeElement(element.id));

    // Add the merged element
    this.editor.timeline.addElementToTrack(targetTrack.id, mergedElement);

    // Ripple subsequent elements if needed
    const rippleAmount =
      mergedElement.durationSeconds -
      elements.reduce((acc, el) => acc + el.durationSeconds, 0);
    if (rippleAmount > 0) {
      const elementsAfter = this.getElementsAfterTime(
        targetTrack,
        mergedElement.time,
        mergedElement,
      );

      this.applyTimeDeltaToElements(elementsAfter, rippleAmount);
    }

    return mergedElement as ElementType;
  }

  /**
   * Merges multiple elements into a single element, filling any gaps between them.
   * Unlike mergeElements, this allows non-contiguous elements to be merged by
   * bridging the gaps in the source media.
   *
   * Example: If element one has trim_start: 0, duration 5 and element two has
   * trim_start: 6, duration 4, there's a 1 second gap in media time (0..5_6..10).
   * This method will create a single element with trim_start: 0, duration 10.
   *
   * @param elements Array of elements to merge
   * @param targetTrack The track where the merged element will reside
   * @returns The merged Element or null if the operation fails
   */
  public bridgeAndMergeElements<
    ElementType extends
      | InstanceType<typeof VideoElement>
      | InstanceType<typeof AudioElement>,
  >(elements: ElementType[], targetTrack: Track): ElementType | null {
    // Validate everything except contiguity
    if (!this.validateMerge(elements, true)) {
      console.warn('Cannot bridge and merge elements: validation failed');
      return null;
    }

    // Calculate properties with gap filling enabled
    const mergedProps = this.calculateMergedProperties(elements, true);

    // Create a new element based on the first element but with merged properties
    // Cast ensures the clone is treated as the same concrete subtype (VideoElement or AudioElement) as the originals
    const mergedElement = elements[0].clone() as ElementType;
    mergedElement.update(mergedProps);

    // Ensure the new element has a unique ID
    mergedElement.id = Element.newKey('bridged');

    // Sort elements by time
    const sortedElements = [...elements].sort((a, b) => a.time - b.time);

    // Calculate the span from start of first to end of last (including gaps)
    const firstElement = sortedElements[0];
    const lastElement = sortedElements[sortedElements.length - 1];
    const totalSpan = lastElement.end_time - firstElement.time;

    // Remove all original elements
    elements.forEach(element => this.editor.timeline.removeElement(element.id));

    // Add the merged element
    this.editor.timeline.addElementToTrack(targetTrack.id, mergedElement);

    // Calculate if subsequent elements need to be rippled
    // This happens when the new merged element covers a different total timeline span
    // than the sum of the individual element durations.  When gaps are bridged the
    // merged element becomes longer, so we need to pull all following elements *earlier*
    // by the bridged gap size so the overall timeline length remains unchanged.

    // account for existing gaps between merged elements -> should ripple by gap size less - use totalSpan not originalSpan
    const rippleAmount = mergedElement.durationSeconds - totalSpan;

    // If the merged element duration increased (gap bridged), shift subsequent elements
    // earlier by the same amount so that the end of the merged range lines up with where
    // the last original element ended.
    if (rippleAmount > 0) {
      const elementsAfter = this.getElementsAfterTime(
        targetTrack,
        lastElement.end_time, // End of last original element
        mergedElement,
      );

      // should shift to the right instead to give space for the merged element? - done
      // Shift earlier – negative delta
      this.applyTimeDeltaToElements(elementsAfter, rippleAmount);
    }

    // Refresh transcript if it's a video element
    // TODO: remove after transcript is fully refactored
    if (mergedElement instanceof VideoElement) {
      mergedElement.invalidateTranscriptSegment();
    }

    return mergedElement;
  }

  /**
   * Splits an element into two at the specified time
   * @param element The element to split
   * @param splitTime The time (in seconds) where the split occurs
   * @returns A tuple of two new Elements or null if the operation fails
   */
  public splitElement(
    element: Element,
    splitTime: number,
  ): [Element, Element] | null {
    if (!element) return null;

    const trackId = this.editor.timeline.getTrackIdForElement(element.id) || '';

    if (!this.validateSplit(element, splitTime)) {
      console.warn('Cannot split element: invalid split time');
      return null;
    }

    const [firstElement, secondElement] = this.createSplitElements(
      element,
      splitTime,
    );

    // Remove the original element
    this.editor.timeline.removeElement(element.id);

    // Get the track and add the two new elements
    const track = this.editor.timeline.getTrack(trackId);
    if (!track) return null;

    this.editor.timeline.addElementToTrack(track.id, firstElement);
    this.editor.timeline.addElementToTrack(track.id, secondElement);

    return [firstElement, secondElement];
  }

  //
  // Utility methods
  //

  /**
   * Gets a track by its ID
   * @param trackId The ID of the track to find
   * @returns The Track object or undefined if not found
   * @deprecated Use direct Track references when possible
   */
  public getTrackById(trackId: string): Track | undefined {
    return this.editor.timeline.tracksMap.get(trackId);
  }

  /**
   * Gets an element by its ID
   * @param elementId The ID of the element to find
   * @returns The Element object or undefined if not found
   * @deprecated Use direct Element references when possible
   */
  public getElementById(elementId: string): Element | undefined {
    return this.editor.timeline.allElements.find(
      element => element.id === elementId,
    );
  }

  /**
   * Finds elements that overlap with a specified time range
   * @param startTime Start of the time range
   * @param endTime End of the time range
   * @param track Optional track to filter elements
   * @param elementType Optional element type to filter (e.g., 'video', 'audio')
   * @returns Array of elements that overlap with the time range
   */
  public findElementsInTimeRange(
    startTime: number,
    endTime: number,
    track?: Track,
    elementType?: string,
  ): Element[] {
    // Get elements from all tracks or a specific track
    let elements: Element[] = [];
    if (track) {
      elements = Array.from(track.elements);
    } else {
      // Use the timeline's allElements property (which is readonly)
      elements = Array.from(this.editor.timeline.allElements);
    }

    // Filter by element type if specified
    if (elementType) {
      elements = elements.filter(el => el.type === elementType);
    }

    // Find elements that overlap with the time range
    // An element overlaps if it starts before the range ends and ends after the range starts
    return elements.filter(element => {
      const elementStart = element.time;
      const elementDuration = element.durationSeconds;
      const elementEnd = elementStart + elementDuration;

      return (
        elementStart < endTime - this.epsilon &&
        elementEnd > startTime + this.epsilon
      );
    });
  }

  /**
   * Updates an element's properties
   * @param element The element to update
   * @param properties Properties to update
   * @returns true if successful, false otherwise
   */
  public adjustElement(
    element: Element,
    properties: Partial<Element>,
  ): boolean {
    element.update(properties);

    return true;
  }

  /**
   * Creates a new element based on a template element
   * @param template The element to use as a template
   * @param properties New properties to apply
   * @returns The newly created element
   */
  public createElementFromTemplate(
    template: Element,
    properties: Partial<Element>,
  ): Element {
    // Clone the template element - this preserves all original properties
    const newElement = template.clone();

    // Apply the new properties
    for (const [key, value] of Object.entries(properties)) {
      // Use type assertions to safely apply properties
      (newElement as any)[key] = value;
    }

    // Ensure a unique ID
    newElement.id =
      properties.id ||
      `element-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;

    return newElement;
  }

  //
  // Utility Functions for Ripple Logic
  //

  /**
   * Retrieves elements on a track that start after a given time, excluding a specific element.
   * @param track The track to search
   * @param time The time threshold
   * @param excludeElement The element to exclude
   * @returns Sorted array of elements starting after the given time
   */
  getElementsAfterTime(
    track: Track,
    time: number,
    excludeElement: Element,
  ): Element[] {
    const threshold = time - this.epsilon;
    return Array.from(track.elements)
      .filter(el => el.id !== excludeElement.id && el.time >= threshold)
      .sort((a, b) => a.time - b.time);
  }

  /**
   * Gets all elements following a specified element on the same track
   * @param element The element to find following elements for
   * @param track Optional track to search in (will look up track if not provided)
   * @returns Array of elements that follow the specified element, sorted by time
   */
  public getFollowingElements(element: Element, track?: Track): Element[] {
    let targetTrack = track;

    if (!targetTrack) {
      const trackId = this.editor.timeline.getTrackIdForElement(element.id);
      if (!trackId) return [];

      targetTrack = this.getTrackById(trackId);
      if (!targetTrack) return [];
    }

    return Array.from(targetTrack.elements)
      .filter(el => el.id !== element.id && el.time >= element.end_time)
      .sort((a, b) => a.time - b.time);
  }

  /**
   * Gets all elements preceding a specified element on the same track
   * @param element The element to find preceding elements for
   * @param track Optional track to search in (will look up track if not provided)
   * @returns Array of elements that precede the specified element, sorted by time
   */
  public getPrecedingElements(element: Element, track?: Track): Element[] {
    let targetTrack = track;

    if (!targetTrack) {
      const trackId = this.editor.timeline.getTrackIdForElement(element.id);
      if (!trackId) return [];

      targetTrack = this.getTrackById(trackId);
      if (!targetTrack) return [];
    }

    return Array.from(targetTrack.elements)
      .filter(el => el.id !== element.id && el.end_time <= element.time)
      .sort((a, b) => a.time - b.time);
  }

  /**
   * Applies a time delta to a list of elements, shifting their start times.
   *
   * A **coupled logo is left alone**: its bounds are derived from its track by
   * `Timeline.adjustCoupledElementsToTrackBounds` at serialization and pushed
   * back by `reapplyCoupledElementBounds`, and `LogoElement.update({ time })`
   * would clear the coupling.
   *
   * The zero floor is applied to the GROUP, not to each element. Clamping each
   * element on its own let one already at 0 absorb a leftward shift while its
   * neighbours took it in full — so the gap between them closed by the part the
   * clamp swallowed. On the transcript track, where consecutive elements are
   * cuts of one recording, that made an element overrun the next; the tokens
   * either side of the cut stopped rising in `ts`, and
   * `ClipTranscriptSegment.filterElements` (which scans `ts` with `findIndex`)
   * then handed the same words to both segments. Shifting by one delta keeps
   * relative spacing whatever the caller asks for: a shift that cannot be
   * honoured in full is reduced for everyone, never for some.
   *
   * @param elements The elements to shift
   * @param timeDelta The amount to shift each element's start time
   */
  applyTimeDeltaToElements(elements: Element[], timeDelta: number): void {
    const shifted = elements.filter(
      el => !(Element.isLogoElement(el) && el.isCoupledToTrack()),
    );
    if (shifted.length === 0) return;

    let delta = timeDelta;
    if (delta < 0) {
      // reduce, not Math.min(...spread): a transcript track can carry
      // thousands of elements and the spread would overflow the stack
      let earliest = Infinity;
      for (const el of shifted) {
        if (el.time < earliest) earliest = el.time;
      }
      delta = Math.max(delta, -earliest);
    }

    for (const el of shifted) {
      el.update({ time: el.time + delta });
    }
  }

  //
  // Internal implementation helpers
  //

  /**
   * Validates that elements can be merged (same track, contiguous, same source)
   * @param elements Array of elements to validate
   * @returns true if the elements can be merged, false otherwise
   */
  validateMerge<T extends VideoElement | AudioElement>(
    elements: T[],
    skipContinuityCheck: boolean = false,
  ): boolean {
    if (elements.length < 2) {
      console.warn('Need at least two elements to merge');
      return false;
    }

    // Check if all elements are on the same track
    const trackIds = new Set(
      elements.map(el => this.editor.timeline.getTrackIdForElement(el.id)),
    );
    if (trackIds.size !== 1) {
      console.warn('Elements must be on the same track to merge');
      return false;
    }

    // For audio/video elements, check if they all have the same source
    if (elements[0].type === 'audio' || elements[0].type === 'video') {
      // Get the source of the first element to compare with others
      const firstSource = (elements[0] as any).source;

      // Check if all elements have the same source
      const differentSources = elements.some(el => {
        return (el as any).source !== firstSource;
      });

      if (differentSources) {
        console.warn('Cannot merge elements with different sources');
        return false;
      }
    }

    // Skip continuity check if specified (for bridgeAndMergeElements)
    if (skipContinuityCheck) {
      return true;
    }

    // Sort elements by time
    const sortedElements = [...elements].sort((a, b) => a.time - b.time);

    // Check if elements are contiguous (no gaps)
    for (let i = 0; i < sortedElements.length - 1; i++) {
      const current = sortedElements[i];
      const next = sortedElements[i + 1];

      const currentEnd = current.time + current.durationSeconds;

      if (Math.abs(currentEnd - next.time) > this.epsilon) {
        console.warn(
          `Elements must be contiguous: gap between ${current.id} and ${next.id}`,
        );
        return false;
      }
    }

    return true;
  }

  /**
   * Calculates properties for the merged element
   * @param elements Array of elements to merge
   * @returns Object with properties for the merged element
   */
  calculateMergedProperties<
    ElementType extends
      | InstanceType<typeof VideoElement>
      | InstanceType<typeof AudioElement>,
  >(
    elements: ElementType[],
    fillGaps: boolean = false,
  ): Partial<Pick<ElementType, 'time' | 'duration' | 'source' | 'trim_start'>> {
    if (elements.length === 0) return {};

    // Sort elements by time
    const sortedElements = [...elements].sort((a, b) => a.time - b.time);

    // Get first and last element as audio/video types
    const firstElement = sortedElements[0];
    const lastElement = sortedElements[sortedElements.length - 1];
    const firstTrimStart = firstElement.trim_start ?? 0;
    const lastTrimStart = lastElement.trim_start ?? 0;

    // Calculate merged duration
    let mergedDuration: number;

    if (fillGaps) {
      // For bridgeAndMergeElements we want the merged element to span the *media* range
      // that all individual clips cover, not just the occupied timeline span.  Using
      // media bounds ensures that any gaps between the source clips (i.e. discontinuities
      // in the underlying video/audio) are included in the new element's duration so
      // that playback is seamless.

      // Example:
      //   clip A: trim_start 0,  duration 5  (media 0 → 5)
      //   clip B: trim_start 6,  duration 4  (media 6 → 10)   ← 1 s gap in media (5 → 6)
      //   timeline positions: A @0-5, B @5-9      (no timeline gap)
      //   merged element should cover media 0 → 10  ⇒ duration 10 (not 9)

      // Therefore we calculate duration using the media space:
      //   (last clip's media end) − (first clip's media start)

      const lastMediaEnd = lastTrimStart + lastElement.durationSeconds;
      mergedDuration = lastMediaEnd - firstTrimStart;
    } else {
      // Standard merge for contiguous elements: use media bounds
      // (last element's media end) - (first element's media start)
      mergedDuration =
        lastTrimStart + lastElement.durationSeconds - firstTrimStart;
    }

    // Base merged properties
    const baseProps = {
      time: firstElement.time,
      duration: mergedDuration,
      source: firstElement.source,
      trim_start: firstTrimStart,
    };

    return baseProps;
  }

  /**
   * Validates that the split time is valid for the given element
   * @param element The element to validate against
   * @param splitTime The time where the split would occur
   * @returns true if the split is valid, false otherwise
   */
  validateSplit(element: Element, splitTime: number): boolean {
    const elementStartTime = element.time;
    const elementEndTime = element.time + element.durationSeconds;

    // Split time must be strictly between start and end times (with small tolerance)
    if (
      splitTime <= elementStartTime + this.epsilon ||
      splitTime >= elementEndTime - this.epsilon
    ) {
      console.warn(
        `Split time ${splitTime} must be between element start ${elementStartTime} and end ${elementEndTime}`,
      );
      return false;
    }

    return true;
  }

  /**
   * Creates two new elements from a split operation
   * @param element The original element to split
   * @param splitTime The time where the split occurs
   * @returns Tuple of two new elements (before and after the split)
   */
  protected createSplitElements(
    element: Element,
    splitTime: number,
  ): [Element, Element] {
    const elementStartTime = element.time;
    const elementDuration = element.durationSeconds;
    const elementEndTime = elementStartTime + elementDuration;

    // Calculate durations for the two new elements
    const firstDuration = splitTime - elementStartTime;
    const secondDuration = elementEndTime - splitTime;

    // Base properties for all element types
    const baseFirstElement = element.clone();
    baseFirstElement.id = Element.newKey('split-first');
    // Use proper setters via editor
    baseFirstElement.time = elementStartTime;
    baseFirstElement.duration = firstDuration;
    if (baseFirstElement.type === 'composition') {
      pickUnderlyingVideoElement(baseFirstElement)?.update({
        id: `inner-${baseFirstElement.id}`,
      });
    }

    const baseSecondElement = element.clone();
    baseSecondElement.id = Element.newKey('split-second');
    // Use proper setters via editor
    baseSecondElement.time = splitTime;
    baseSecondElement.duration = secondDuration;
    if (baseSecondElement.type === 'composition') {
      pickUnderlyingVideoElement(baseSecondElement)?.update({
        id: `inner-${baseSecondElement.id}`,
      });
    }

    // Handle properties that are specific to video/audio elements
    if (
      element.type === 'video' ||
      element.type === 'audio' ||
      element.type === 'composition'
    ) {
      const mediaElement = element as VideoElement | AudioElement;

      // Update trim_start for second element
      const trimSourceElement = pickUnderlyingVideoElement(mediaElement);
      const firstTrimEnd = (trimSourceElement?.trim_start ?? 0) + firstDuration;
      const trimDestinationElement =
        pickUnderlyingVideoElement(baseSecondElement);
      trimDestinationElement?.update({ trim_start: firstTrimEnd } as Element);

      (baseFirstElement as VideoElement | AudioElement).audio_fade_out = 0;
      (baseSecondElement as VideoElement | AudioElement).audio_fade_in = 0;

      // Ensure transcript segment is refreshed
      // TODO: Do we need to do thius?
      // videoElement.invalidateTranscriptSegment();
    }

    // Clear logo coupling on split elements — split pieces no longer span the
    // full coupled track so they must become independent to allow separate
    // movement and configuration.
    if (Element.isLogoElement(baseFirstElement)) {
      baseFirstElement.coupledTrackId = null;
    }
    if (Element.isLogoElement(baseSecondElement)) {
      baseSecondElement.coupledTrackId = null;
    }

    Element.stripSeamAnimations(baseFirstElement, { exit: true });
    Element.stripSeamAnimations(baseSecondElement, { enter: true });

    return [baseFirstElement, baseSecondElement];
  }

  /**
   * Excises a specific media time range from a VideoElement, creating two new elements
   * that exclude the removed range while maintaining seamless timeline with a cut in
   * the source playback.
   *
   * @param element The VideoElement to excise from.
   * @param mediaRemovalStart The start time in media seconds to remove.
   * @param mediaRemovalEnd The end time in media seconds to remove.
   * @param mode Optional TimelineMode to use for this operation.
   * @returns An object containing the two new elements and the removed duration, or null if unsuccessful.
   */
  public exciseTimeRangeFromElement(
    element: Element,
    mediaRemovalStart: number,
    mediaRemovalEnd: number,
    mode: TimelineMode = TimelineMode.DEFAULT,
  ): {
    firstPart: Element;
    secondPart: Element;
    removedDuration: number;
  } | null {
    if (!element) return null;

    if (element.type !== 'video' && element.type !== 'composition') {
      console.warn(`Element ${element.id} is not a VideoElement.`);
      return null;
    }

    const trackId = this.editor.timeline.getTrackIdForElement(element.id);
    if (!trackId) return null;

    const track = this.getTrackById(trackId);
    if (!track) return null;

    // Trim Adjusted media start and end times
    const elementMediaStart =
      pickUnderlyingVideoElement(element)?.trim_start ?? 0;
    // const elementMediaEnd = elementMediaStart + element.durationSeconds;

    // EXAMPLE
    // Original duration: 10
    // mediaRemovalStart: 2
    // mediaRemovalEnd: 5

    // Description: Create two new elements:
    // 1. First part: { time: 0, duration: 2, trim_start: 0 }
    // 2. Second part: { time: 2, duration: 5, trim_start: 7 }
    // 3. Removed duration: 3
    // 4. New duration: 10 - 3 = 7
    // 5. New second trim_start: mediaRemovalStart + firstPartDuration + removedDuration
    //                                   2 + 2 + 3 = 7

    // Calculate durations
    // Removed duration is the difference between media removal start and end
    const removedDuration = mediaRemovalEnd - mediaRemovalStart;

    // First element duration (before the cut)
    // Duration is simply the cut point
    // ex: Original element should go from 0 to cut point (2)
    const firstPartDuration = mediaRemovalStart;
    // Trim start is unchanged
    // ex: original trim start 2, keep it the same
    const firstPartTrimStart = elementMediaStart; // Trim start is unchanged.

    // Second element duration (after the cut)
    // Duration is the original duration minus the removed duration and the first part duration
    // ex: original duration 10 - cut point 2 - removed duration 2 = 6
    const secondPartDuration =
      element.durationSeconds - mediaRemovalStart - removedDuration;
    // Trim start is the cut point
    // ex: original trim start 2, cut at 5, removed duration 2
    // second part trim start = 2 + 5 + 2 = 9
    const secondPartTrimStart =
      elementMediaStart + firstPartDuration + removedDuration;

    // Validate new duration
    if (secondPartDuration <= 0) {
      console.warn(
        `Invalid second part duration: ${secondPartDuration}. Cannot excise time range.`,
      );
      return null;
    }

    // Create first part
    const firstPart = element.clone();
    firstPart.update({
      id: Element.newKey('excised-first'),
      duration: firstPartDuration,
      time: element.time,
    });
    if (firstPart.type === 'composition') {
      pickUnderlyingVideoElement(firstPart)?.update({
        id: `inner-${firstPart.id}`,
      });
    }
    pickUnderlyingVideoElement(firstPart)?.update({
      trim_start: firstPartTrimStart,
    });

    // Create second part
    const secondPart = element.clone();
    secondPart.update({
      id: Element.newKey('excised-second'),
      duration: secondPartDuration,
      time: element.time + firstPartDuration,
    });
    pickUnderlyingVideoElement(secondPart)?.update({
      trim_start: secondPartTrimStart,
    });
    if (secondPart.type === 'composition') {
      pickUnderlyingVideoElement(secondPart)?.update({
        id: `inner-${secondPart.id}`,
      });
    }

    this.editor.timeline.removeElement(element.id);
    this.editor.timeline.addElementToTrack(track.id, firstPart);
    this.editor.timeline.addElementToTrack(track.id, secondPart);

    // Add Transcript to new elements.
    // TODO: Refactor Transcript segment management outside of VideoElement

    if (mode == TimelineMode.RIPPLE) {
      // Move all subsequent elements by the removed duration
      const affectedElements = this.getElementsAfterTime(
        track,
        secondPart.end_time,
        element,
      );
      this.applyTimeDeltaToElements(affectedElements, -removedDuration);
    }

    return { firstPart, secondPart, removedDuration };
  }

  /**
   *  Undo the insertion of an element by shifting subsequent elements back
   *  to their original positions and removing the inserted element.
   * @param element
   * @param shiftAmount
   * @param track
   */
  public undoInsertElement(
    element: Element,
    shiftAmount: number,
    track: Track,
  ): void {
    const elementsAfter = this.getElementsAfterTime(
      track,
      element.time,
      element,
    );
    this.applyTimeDeltaToElements(elementsAfter, -shiftAmount);
    this.removeElement(element, TimelineMode.DEFAULT);
  }

  /**
   * Reverses a merge operation by removing the merged element and restoring the original elements.
   * @param mergedElement The merged element to remove
   * @param originalElements The array of original elements to restore
   * @param track The track containing the elements
   */
  public undoMergeElements(
    mergedElement: Element,
    originalElements: Element[],
    track: Track,
  ): void {
    // Remove the merged element from the timeline
    this.removeElement(mergedElement, TimelineMode.DEFAULT);

    // Restore the original elements to the track
    originalElements.forEach(element => {
      this.editor.timeline.addElementToTrack(track.id, element);
    });

    // Ripple difference between merged and original elements
    const rippleAmount =
      originalElements.reduce((acc, el) => acc + el.durationSeconds, 0) -
      mergedElement.durationSeconds;

    const elementsAfter = this.getElementsAfterTime(
      track,
      mergedElement.end_time,
      originalElements[originalElements.length - 1],
    );
    this.applyTimeDeltaToElements(elementsAfter, rippleAmount);

    // Recalculate timeline duration
    this.editor.timeline.recalculateDuration();
  }

  /**
   * Reverses a trim operation, restoring the element’s original state and reversing any ripple effect.
   * @param element The element that was trimmed
   * @param originalState The element’s state before the trim (e.g., { trim_start, duration })
   * @param track The track containing the element
   * @param rippleMode The mode used during the trim (e.g., RIPPLE or DEFAULT)
   */
  public undoTrimElement(
    element: Element,
    originalState: Record<string, any>,
    track: Track,
    rippleMode: TimelineMode,
  ): void {
    // Calculate the duration difference caused by the trim
    const originalDuration = originalState.duration;
    const newDuration = element.durationSeconds;
    const durationDiff = newDuration - originalDuration;

    // Restore the element to its original state (trim_start, duration, etc.)
    element.restoreState(originalState);

    // If ripple mode was used, reverse the ripple effect on subsequent elements
    if (rippleMode === TimelineMode.RIPPLE) {
      // Get elements after this one on the track
      const elementsAfter = this.getElementsAfterTime(
        track,
        element.end_time,
        element,
      );
      // Shift them back by the inverse of the duration change
      this.applyTimeDeltaToElements(elementsAfter, -durationDiff);
    }
  }

  /*
   * Shifts a track up or down in the layer stack
   * @param trackId ID of the track to shift
   * @param direction Direction to shift the track ('up' or 'down')
   * @returns ID of the target track if success, or null if failed
   */
  public shiftTrackLayer(
    trackId: string,
    direction: 'up' | 'down',
  ): string | null {
    // Validate track ID is within allowed range (1-31)
    const trackIdNum = Number(trackId);
    if (isNaN(trackIdNum) || trackIdNum < 1 || trackIdNum > 31) return null;

    // sorting is needed in case track was just added ('3')
    // and the trackId is not in the correct position yet - ('1', '2', '32', '3')
    // order will be implicitly enforced after Timeline.fromJSON is called
    const tracks = [...this.editor.timeline.tracks].sort((a, b) => {
      return Number(a.id) - Number(b.id); // Sort by numeric ID
    });
    const trackIndex = tracks.findIndex(track => track.id === trackId);

    if (trackIndex === -1) return null;

    // Determine target track based on position
    let targetIndex: number;
    if (direction === 'up') {
      // Can't move the top track up further
      if (trackIndex >= tracks.length - 1) return null;
      targetIndex = trackIndex + 1;
    } else {
      // Can't move the bottom track down further
      if (trackIndex <= 0) return null;
      targetIndex = trackIndex - 1;
    }

    const targetTrack = tracks[targetIndex];
    if (!targetTrack) return null;
    // Validate target track ID is within allowed range (1-31)
    const targetTrackIdNum = Number(targetTrack.id);
    if (
      isNaN(targetTrackIdNum) ||
      targetTrackIdNum < 1 ||
      targetTrackIdNum > 31
    )
      return null;

    const targetTrackId = targetTrack.id;
    const success = this.swapTrackPositions(trackId, targetTrackId);

    if (success) return targetTrackId;

    return null;
  }

  /**
   * Ensures proper z-index assignment for all elements on a track
   * Skips assignment if scenes are present (LayoutEngine handles it)
   * @param trackId ID of the track to update z-index for
   */
  public ensureProperZIndexPositioning(trackId: string): void {
    const track = this.editor.timeline.getTrack(trackId);
    if (!track) return;

    // If scenes are present, skip - LayoutEngine handles z_index assignment
    if (this.editor.timeline.scenes.length > 0) {
      return;
    }

    for (const element of track.elements) {
      // Skip elements without a track property
      if (element.track === null || element.track === undefined) continue;

      let zIndex: number;

      // Special case for layout compositions (element's track >= 90)
      if (element.type === 'composition' && element.track >= 90) {
        zIndex = -1000;
      } else {
        // Regular elements get z_index matching their track
        zIndex = element.track;
      }

      // Update the element's z_index property
      element.update({ z_index: zIndex });
    }
  }

  /**
   * Swaps the positions of two tracks in the layer stack
   * @param trackId1 ID of the first track
   * @param trackId2 ID of the second track
   * @returns true if successful, false otherwise
   */
  public swapTrackPositions(trackId1: string, trackId2: string): boolean {
    const timeline = this.editor.timeline;
    const track1 = timeline.getTrack(trackId1);
    const track2 = timeline.getTrack(trackId2);

    if (!track1 || !track2) return false;

    // Store original IDs
    const track1Id = track1.id;
    const track2Id = track2.id;

    // Update track1 ID and its elements
    track1.setId(track2Id);
    for (const element of track1.elements) {
      element.update({ track: Number(track2Id) });
    }

    // Update track2 ID and its elements
    track2.setId(track1Id);
    for (const element of track2.elements) {
      element.update({ track: Number(track1Id) });
    }

    // Remap scene bindings and coupled logos without reinitializing
    timeline.swapTrackIds(track1, track2, track1Id, track2Id);
    timeline.sortTracksByNumericId();
    this.resyncTranscriptTrackId();

    // Ensure proper z-index assignment for both tracks after swapping
    this.ensureProperZIndexPositioning(track1Id);
    this.ensureProperZIndexPositioning(track2Id);

    return true;
  }

  /**
   * Re-points the editor's transcript track id after tracks are renumbered —
   * it is stored by id, so a renumber leaves it naming another track.
   */
  public resyncTranscriptTrackId(): void {
    const transcriptTrack = this.editor.getVideoTranscriptTrack();
    if (transcriptTrack) this.editor.setTranscriptTrack(transcriptTrack.id);
  }

  set extraElementDataRaw(value: Video['extraElementData']) {
    this._extraElementData = new ExtraElementData(value);
  }

  get extraElementDataRaw(): Video['extraElementData'] {
    return this._extraElementData?.value ?? {};
  }

  get extraElementData(): ExtraElementData {
    return this._extraElementData || new ExtraElementData({});
  }

  /**
   * Gets the punch list items map (runtime domain models).
   */
  get punchListItems(): Map<string, PunchListItem> {
    return this._punchListItems;
  }

  isPunchListElement(elementId: string): boolean {
    return this._punchListItems.has(elementId);
  }

  hydratePunchListItems(
    transcript: TranscriptToken[],
    items: PunchListItemData[],
  ): void {
    this._punchListItems.clear();

    for (const itemData of items) {
      const item = PunchListItem.fromData(itemData, transcript);
      if (item) {
        this._punchListItems.set(item.elementId, item);
      }
      // Items with invalid indices are skipped (logged in fromData)
    }
  }

  /**
   * Converts ImageElements that are marked as logos into LogoElement instances.
   * Should be called after loading a video source (after Timeline.fromJSON).
   */
  convertMarkedLogosToLogoElements(): void {
    const timeline = this.editor.timeline;

    for (const track of timeline.tracks) {
      for (const el of track.elements) {
        if (
          !Element.isImageElement(el) ||
          Element.isLogoElement(el) ||
          !this.extraElementData.isMarkedAsLogo(el)
        ) {
          continue;
        }

        // Convert to LogoElement
        const logoElement = LogoElement.fromImageElement(el);
        track.replaceElement(el.id, logoElement);
      }
    }
  }

  /**
   * Identifies and marks elements that are temporally coupled to the main video track.
   * Should be called after loading a video source to establish initial coupling relationships.
   * Uses a tolerance of 0.1 seconds to account for slight timing differences.
   *
   * Only LogoElement instances can be coupled; regular ImageElements are ignored.
   */
  identifyAndMarkCoupledElements(): void {
    const COUPLING_TOLERANCE = 0.1; // 100ms tolerance for initial coupling detection

    // Get main video track
    const transcriptTrack =
      this.editor.getTranscriptTrack() ?? this.editor.timeline.getVideoTrack();
    if (!transcriptTrack) return;

    const mainVideoElements = Array.from(
      transcriptTrack.elements.values(),
    ).filter(Element.isVideoElement);
    if (mainVideoElements.length === 0) return;

    // Calculate main video track bounds
    let mainVideoTrackStart = Infinity;
    let mainVideoTrackEnd = -Infinity;
    for (const videoEl of mainVideoElements) {
      mainVideoTrackStart = Math.min(mainVideoTrackStart, videoEl.time);
      mainVideoTrackEnd = Math.max(
        mainVideoTrackEnd,
        videoEl.time + videoEl.durationSeconds,
      );
    }
    const mainVideoTrackDuration = mainVideoTrackEnd - mainVideoTrackStart;

    // Check all elements for logo coupling
    for (const el of this.editor.timeline.allElements) {
      if (!Element.isLogoElement(el)) continue;

      const logoStart = el.time;
      const logoEnd = el.time + el.durationSeconds;
      const logoDuration = el.durationSeconds;

      // Check if logo is coupled to the track
      const startsWithTrack =
        Math.abs(logoStart - mainVideoTrackStart) < COUPLING_TOLERANCE;
      const endsWithTrack =
        Math.abs(logoEnd - mainVideoTrackEnd) < COUPLING_TOLERANCE;
      const matchesTrackDuration =
        Math.abs(logoDuration - mainVideoTrackDuration) < COUPLING_TOLERANCE;

      const isCoupled =
        startsWithTrack && endsWithTrack && matchesTrackDuration;

      // Set or clear the coupled track ID
      el.coupledTrackId = isCoupled ? transcriptTrack.id : null;
    }
  }
}
